import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Typography, Box, Card, CardContent, IconButton, Select, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, FormControl, InputLabel } from '@mui/material';
import { CommunityBoardDAO } from '../../../repository/CommunityBoardDAO';
import { useUserContext } from '../../../context/user_context/UserContext';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Edit as EditIcon, Reply as ReplyIcon, ArrowBack as ArrowBackIcon, Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon, Delete as DeleteIcon, Report as ReportIcon, Gavel as GavelIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Loading } from '../../../components/loading/Loading';
import EditPostModal from './modals/EditPostModal';
import Comment from './components/Comment';
import { Timestamp } from 'firebase/firestore';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { StringKeys } from '../../../resources/strings/StringKeys';

const ThreadView = () => {
  const { threadId } = useParams();
  const location = useLocation();
  const [post, setPost] = useState(location.state?.post || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState('top');
  const [userVotes, setUserVotes] = useState({});
  const { firebaseUser } = useUserContext();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportingContentId, setReportingContentId] = useState(null);
  const [reportingContentType, setReportingContentType] = useState(null);
  const [moderationMenuAnchor, setModerationMenuAnchor] = useState(null);
  const [selectedContentForModeration, setSelectedContentForModeration] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //TODO:String Resources Translations

  // Add report reason options
  const REPORT_REASONS = {
    SPAM: 'spam',
    HARASSMENT: 'harassment',
    HATE_SPEECH: 'hate_speech',
    MISINFORMATION: 'misinformation',
    INAPPROPRIATE: 'inappropriate',
    OFF_TOPIC: 'off_topic',
    OTHER: 'other'
  };

  const REPORT_REASON_LABELS = {
    spam: 'Spam or Advertising',
    harassment: 'Harassment or Bullying',
    hate_speech: 'Hate Speech',
    misinformation: 'Misinformation',
    inappropriate: 'Inappropriate Content',
    off_topic: 'Off-Topic',
    other: 'Other'
  };

  const handleVote = async (postId, _incrementValue, isUpvote) => {
    if (!firebaseUser) {
      showToast('You must be logged in to vote', 'error');
      return;
    }

    let voteChange = 0;
    try {
      const existingVote = userVotes?.[postId];
      
      // If clicking the same vote type (e.g. upvote → remove upvote)
      if (existingVote === isUpvote) {
        // If we're removing an upvote, we need to subtract 1
        // If we're removing a downvote, we need to add 1
        voteChange = existingVote ? -1 : 1;
      }
      // If changing vote type (e.g. upvote → downvote)
      else if (existingVote !== undefined && existingVote !== null) {
        // If switching from upvote to downvote: -2
        // If switching from downvote to upvote: +2
        voteChange = isUpvote ? 2 : -2;
      }
      // If no previous vote
      else {
        // New upvote: +1
        // New downvote: -1
        voteChange = isUpvote ? 1 : -1;
      }

      // Update local state optimistically
      setPost(prevPost => ({
        ...prevPost,
        vote_score: (prevPost.vote_score || 0) + voteChange
      }));

      // Make the API call
      await CommunityBoardDAO.voteOnPost(postId, firebaseUser.uid, voteChange, isUpvote);
    } catch (error) {
      // Revert optimistic update on error
      setPost(prevPost => ({
        ...prevPost,
        vote_score: (prevPost.vote_score || 0) - voteChange
      }));
      console.error('Error voting:', error);
      showToast('Failed to vote', 'error');
    }
  };

  const fetchMessages = useCallback(async (isInitialFetch = false) => {
    if (messagesLoading || (!isInitialFetch && !hasMore)) return;

    setMessagesLoading(true);
    try {
      const result = await CommunityBoardDAO.fetchMessagesForPost(threadId, { sortBy, lastVisible: isInitialFetch ? null : lastVisible });

      setMessages(prevMessages => {
        if (isInitialFetch) return result.messages;
        const existingIds = new Set(prevMessages.map(m => m.id));
        const uniqueNewMessages = result.messages.filter(msg => !existingIds.has(msg.id));
        return [...prevMessages, ...uniqueNewMessages];
      });
      setLastVisible(result.lastVisible);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error fetching messages:', error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG), ToastType.DANGER);
    } finally {
      setMessagesLoading(false);
    }
  }, [threadId, sortBy, lastVisible, messagesLoading, hasMore]);

  const handleLoadMore = useCallback(() => {
    if (!messagesLoading && hasMore) {
      fetchMessages(false);
    }
  }, [messagesLoading, hasMore, fetchMessages]);

  useEffect(() => {
    setMessages([]);
    setLastVisible(null);
    setHasMore(true);
    fetchMessages(true);
  }, [threadId, sortBy]);

  const renderLoadingIndicator = () => {
    if (messagesLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (!messagesLoading && hasMore) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={messagesLoading}
            sx={{ 
              minWidth: { xs: 150, sm: 200 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              p: { xs: '6px 12px', sm: '8px 16px' }
            }}
            size="small"
          >
            Load More Comments
          </Button>
        </Box>
      );
    }
    if (!hasMore && messages.length > 0) {
      return (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            py: 4,
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          No more comments to load
        </Typography>
      );
    }
    return null;
  };

  useEffect(() => {
    let unsubscribeMessages = null;

    const fetchData = async () => {
      try {
        const { postDoc, collectionName } = await CommunityBoardDAO.getPost(threadId);
        if (postDoc && postDoc.exists()) {
          setPost({ id: postDoc.id, ...postDoc.data() });
        }
        const { messages: initialMessages, lastVisible: initialLastVisible, hasMore: initialHasMore } = 
          await CommunityBoardDAO.fetchMessagesForPost(threadId, {
            sortBy: sortBy
          });
          
        setMessages(initialMessages || []);
        setLastVisible(initialLastVisible);
        setHasMore(initialHasMore);
      } catch (error) {
        console.error('Error in ThreadView:', error);
        setMessages([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
    };
  }, [threadId, sortBy]);

  // Update useEffect for user votes
  useEffect(() => {
    if (!firebaseUser) return;
    const unsubscribeVotes = CommunityBoardDAO.subscribeToUserVotes(firebaseUser.uid, (votesData) => { setUserVotes(votesData); });
    return () => unsubscribeVotes && unsubscribeVotes();
  }, [firebaseUser]);

  // Update the useEffect to check saved status
  useEffect(() => {
    if (!firebaseUser || !threadId) return;
    
    // Check if post is saved
    CommunityBoardDAO.isPostSaved(threadId, firebaseUser.uid)
      .then(saved => {
        setIsSaved(saved);
      })
      .catch(error => {
        console.error('Error checking saved status:', error);
      });
  }, [firebaseUser, threadId]);

  const handleAddReply = async () => {
    if (!replyContent.trim()) {
      showToast('Reply cannot be empty', 'error');
      return;
    }

    try {
      const newReplyId = await CommunityBoardDAO.addMessage(
        threadId,
        {
          message: replyContent,
          creator: firebaseUser 
            ? `${firebaseUser.first_name || ''} ${firebaseUser.last_name || ''}`.trim() 
            : 'Anonymous',
          creator_id: firebaseUser ? firebaseUser.uid : null,
        }
      );

      // Create the new reply object
      const newReply = {
        id: newReplyId,
        message: replyContent,
        creator: firebaseUser 
          ? `${firebaseUser.first_name || ''} ${firebaseUser.last_name || ''}`.trim() 
          : 'Anonymous',
        creator_id: firebaseUser ? firebaseUser.uid : null,
        created_at: Timestamp.now(),
        vote_score: 1,
        replies: []
      };

      // Add the new reply to the beginning of the messages array
      setMessages(prevMessages => [newReply, ...prevMessages]);
      
      setReplyContent('');
      setShowReplyForm(false);
      showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
    } catch (error) {
      console.error('Error adding reply:', error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
    }
  };

  const handleSavePost = async () => {
    if (!firebaseUser) {
      showToast('You must be logged in to save posts', 'error');
      return;
    }

    try {
      await CommunityBoardDAO.savePost(threadId, firebaseUser.uid, !isSaved);
      setIsSaved(!isSaved);
      showToast(isSaved ? 'Post removed from saved' : 'Post saved successfully', 'success');
    } catch (error) {
      console.error('Error saving post:', error);
      showToast('Failed to save post', 'error');
    }
  };

  const handleDeletePost = async () => {
    if (!firebaseUser || firebaseUser.uid !== post.creator_id) {
      showToast('You can only delete your own posts', 'error');
      return;
    }

    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    try {
      await CommunityBoardDAO.softDeletePost(threadId);
      setPost(prevPost => ({
        ...prevPost,
        deleted: true,
        deleted_at: Timestamp.now()
      }));
      showToast('Post deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting post:', error);
      showToast('Failed to delete post', 'error');
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason) {
      showToast('Please select a reason for reporting', 'error');
      return;
    }

    try {
      await CommunityBoardDAO.reportContent(
        reportingContentId,
        firebaseUser.uid,
        reportingContentType,
        reportReason
      );
      showToast('Content reported successfully', 'success');
      setReportDialogOpen(false);
      setReportReason('');
      setReportingContentId(null);
      setReportingContentType(null);
    } catch (error) {
      console.error('Error reporting content:', error);
      showToast('Failed to report content', 'error');
    }
  };

  const handleModerateContent = async (action) => {
    if (!selectedContentForModeration) return;

    try {
      await CommunityBoardDAO.moderateContent(
        selectedContentForModeration.id,
        selectedContentForModeration.type,
        action,
        firebaseUser.uid
      );

      if (selectedContentForModeration.type === 'post') {
        setPost(prevPost => ({
          ...prevPost,
          deleted: true,
          deleted_at: Timestamp.now(),
          deleted_by: firebaseUser.uid,
          moderation_action: action
        }));
      } else {
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === selectedContentForModeration.id
              ? {
                  ...msg,
                  deleted: true,
                  deleted_at: Timestamp.now(),
                  deleted_by: firebaseUser.uid,
                  moderation_action: action
                }
              : msg
          )
        );
      }

      showToast('Content moderated successfully', 'success');
    } catch (error) {
      console.error('Error moderating content:', error);
      showToast('Failed to moderate content', 'error');
    } finally {
      setModerationMenuAnchor(null);
      setSelectedContentForModeration(null);
    }
  };

  return (
    <>
      {messages && post ? (
        <AppSidebar
          content={
            <Box sx={{ margin: '0 auto', p: { xs: 0, sm: 2 } }}>
              {/* Back arrow and title */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1,
                  mb: { xs: 1, sm: 2 },
                  pl: { xs: 1, sm: 0 }
                }}
              >
                <IconButton 
                  onClick={() => navigate('/biziboard')}
                  sx={{ p: { xs: 0.5, sm: 1 } }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography 
                  variant="h6" 
                  component="h1" 
                  sx={{ 
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    fontWeight: 500,
                    flexGrow: 1
                  }}
                >
                  {post.deleted ? '[deleted]' : post.topic}
                </Typography>
              </Box>

              {/* Post Card with enhanced mobile layout */}
              <Card 
                elevation={0}
                sx={{ 
                  mb: 2,
                  borderRadius: { xs: 0, sm: 1 },
                  border: 'none',
                  borderBottom: { xs: '1px solid', sm: 'none' },
                  borderColor: 'divider',
                  width: '100%',
                  maxWidth: '100%'
                }}
              >
                <CardContent sx={{ 
                  p: { xs: 2, sm: 3 },
                  '&:last-child': { pb: { xs: 2, sm: 3 } },
                  width: '100%'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 2 },
                    width: '100%'
                  }}>
                    {/* Vote buttons - more compact on mobile */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      minWidth: { xs: 32, sm: 40 }
                    }}>
                      <IconButton
                        onClick={() => handleVote(post.id, 1, true)}
                        color={userVotes?.[post.id] === true ? 'primary' : 'default'}
                        disabled={post.deleted}
                        sx={{ 
                          p: { xs: 0.5, sm: 1 },
                          '&:hover': { bgcolor: 'transparent' }
                        }}
                      >
                        <ArrowUpIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                      </IconButton>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          fontWeight: 500
                        }}
                      >
                        {post.vote_score || 0}
                      </Typography>
                      <IconButton
                        onClick={() => handleVote(post.id, -1, false)}
                        color={userVotes?.[post.id] === false ? 'error' : 'default'}
                        disabled={post.deleted}
                        sx={{ 
                          p: { xs: 0.5, sm: 1 },
                          '&:hover': { bgcolor: 'transparent' }
                        }}
                      >
                        <ArrowDownIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </Box>

                    <Box sx={{ 
                      flexGrow: 1,
                      minWidth: 0, // This is crucial for text wrapping
                      width: '100%'
                    }}>
                      {/* Post metadata */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          mb: 0.5
                        }}
                      >
                        Posted by {post.deleted ? '[deleted]' : post.creator} • {formatDistanceToNow(post.created_at.toDate())} ago
                      </Typography>

                      {/* Post content */}
                      <Typography 
                        variant="body1" 
                        component="div"
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          lineHeight: { xs: 1.4, sm: 1.6 },
                          width: '100%',
                          '& p': { 
                            m: 0,
                            mb: 2,
                            overflowWrap: 'break-word',
                            wordWrap: 'break-word',
                            hyphens: 'auto',
                            width: '100%'
                          },
                          '& h3': {
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            fontWeight: 600,
                            mt: 2,
                            mb: 2,
                            width: '100%'
                          },
                          '& ul, & ol': {
                            pl: 3,
                            mb: 2,
                            width: '100%',
                            boxSizing: 'border-box'
                          },
                          '& li': {
                            mb: 1,
                            width: '100%',
                            overflowWrap: 'break-word',
                            wordWrap: 'break-word'
                          },
                          '& strong': {
                            fontWeight: 600
                          },
                          '& br': {
                            display: 'none'
                          },
                          '& div': {
                            width: '100%',
                            maxWidth: '100%',
                            overflowWrap: 'break-word',
                            wordWrap: 'break-word',
                            '& > *:last-child': {
                              mb: 0
                            }
                          }
                        }}
                      >
                        {post.deleted ? (
                          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            [This post has been deleted]
                          </Typography>
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
                        )}
                      </Typography>

                      {/* Action buttons - more compact on mobile */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: 2,
                          mt: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          pt: 1,
                          alignItems: 'center'
                        }}
                      >
                        {!post.deleted && (
                          <>
                            <Button
                              startIcon={<ReplyIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                              onClick={() => setShowReplyForm(true)}
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                textTransform: 'none',
                                color: 'text.secondary',
                                p: { xs: '4px 8px', sm: '6px 16px' },
                                minWidth: { xs: 'auto', sm: 0 }
                              }}
                            >
                              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Reply</Box>
                            </Button>

                            <Button
                              onClick={() => {
                                setReportingContentId(post.id);
                                setReportingContentType('post');
                                setReportDialogOpen(true);
                              }}
                              startIcon={<ReportIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                textTransform: 'none',
                                color: 'text.secondary',
                                p: { xs: '4px 8px', sm: '6px 16px' },
                                minWidth: { xs: 'auto', sm: 0 }
                              }}
                            >
                              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Report</Box>
                            </Button>
                            <Button
                            onClick={handleSavePost}
                            startIcon={isSaved ? <BookmarkIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} /> : <BookmarkBorderIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              textTransform: 'none',
                              color: isSaved ? 'primary.main' : 'text.secondary',
                              p: { xs: '4px 8px', sm: '6px 16px' },
                              minWidth: { xs: 'auto', sm: 0 }
                            }}
                          >
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{isSaved ? 'Saved' : 'Save'}</Box>
                          </Button>
                          <Button
                                onClick={() => setShowEditModal(true)}
                                startIcon={<EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  textTransform: 'none',
                                  color: 'text.secondary',
                                  p: { xs: '4px 8px', sm: '6px 16px' },
                                  minWidth: { xs: 'auto', sm: 0 }
                                }}
                              >
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Edit</Box>
                              </Button>
                          </>
                        )}

                        <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
                          {firebaseUser?.uid === post.creator_id && !post.deleted && (
                            <>
                              <Button
                                onClick={handleDeletePost}
                                startIcon={<DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  textTransform: 'none',
                                  color: 'error.main',
                                  p: { xs: '4px 8px', sm: '6px 16px' },
                                  minWidth: { xs: 'auto', sm: 0 }
                                }}
                              >
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Delete</Box>
                              </Button>
                              {firebaseUser?.biziboard_admin && (
                              <Button
                                onClick={(e) => {
                                  setSelectedContentForModeration({ id: post.id, type: 'post' });
                                  setModerationMenuAnchor(e.currentTarget);
                                }}
                                startIcon={<GavelIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  textTransform: 'none',
                                  color: 'green',
                                  p: { xs: '4px 8px', sm: '6px 16px' },
                                  minWidth: { xs: 'auto', sm: 0 }
                                }}
                              >
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Mod Tools</Box>
                              </Button>
                            )}
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Reply Form */}
                      {showReplyForm && (
                        <Box 
                          sx={{ 
                            mt: 2,
                            mb: 2,
                            p: { xs: 1, sm: 2 },
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Add a reply
                          </Typography>
                          <Box sx={{ 
                            '& .quill': { 
                              height: 'auto',
                              marginBottom: 1,
                              maxWidth: '100%',
                              '& .ql-editor': {
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                padding: { xs: '8px', sm: '12px' },
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                minHeight: { xs: '100px', sm: '120px' },
                                maxHeight: '300px',
                                overflowY: 'auto'
                              },
                              '& .ql-container': {
                                maxWidth: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '4px',
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              },
                              '& .ql-toolbar': {
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '4px',
                                marginBottom: '8px',
                                padding: { xs: '4px', sm: '8px' }
                              }
                            }
                          }}>
                            <ReactQuill
                              ref={quillRef}
                              value={replyContent}
                              onChange={setReplyContent}
                              placeholder="Write your reply..."
                              modules={{
                                toolbar: [
                                  ['bold', 'italic'],
                                  ['clean']
                                ]
                              }}
                            />
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: 'flex-end',
                            mt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            pt: { xs: 1, sm: 2 }
                          }}>
                            <Button 
                              variant="contained"
                              onClick={handleAddReply}
                              disabled={!replyContent.trim()}
                              size="small"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              Reply
                            </Button>
                            <Button 
                              onClick={() => {
                                setShowReplyForm(false);
                                setReplyContent('');
                              }}
                              size="small"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Comment sorting - more compact on mobile */}
              <Box 
                sx={{ 
                  px: { xs: 1.5, sm: 0 },
                  mb: { xs: 1, sm: 2 }, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  borderBottom: { xs: '1px solid', sm: 'none' },
                  borderColor: 'divider',
                  pb: { xs: 1, sm: 0 }
                }}
              >
                <Typography 
                  variant="subtitle1"
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 500
                  }}
                >
                  Comments
                </Typography>
                <Select
                  size="small"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '.MuiSelect-select': { 
                      py: { xs: 0.5, sm: 0.75 }
                    }
                  }}
                >
                  <MenuItem value="top" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Top</MenuItem>
                  <MenuItem value="new" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>New</MenuItem>
                </Select>
              </Box>

              {/* Comments section */}
              <Box sx={{ mt: 2 }}>
                {messages.map((message) => (
                  <Comment
                    key={message.id}
                    comment={message}
                    postId={threadId}
                    threadId={threadId}
                    setMessages={setMessages}
                    userVotes={userVotes}
                    onVoteChange={setUserVotes}
                    onReport={(commentId) => {
                      setReportingContentId(commentId);
                      setReportingContentType('comment');
                      setReportDialogOpen(true);
                    }}
                    onModerate={(commentId, event) => {
                      setSelectedContentForModeration({ id: commentId, type: 'comment' });
                      setModerationMenuAnchor(event.currentTarget);
                    }}
                    isAdmin={firebaseUser?.biziboard_admin}
                  />
                ))}
              </Box>

              {/* Load More Comments Button */}
              {renderLoadingIndicator()}

              {/* Existing modals */}
              <EditPostModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                post={post}
                onPostEdited={() => {
                  CommunityBoardDAO.getPost(threadId).then(doc => {
                    if (doc.exists()) {
                      setPost({ id: doc.id, ...doc.data() });
                    }
                  });
                }}
              />
            </Box>
          }
        />
      ) : (
        <AppSidebar content={<Loading/>}/>
      )}

      {/* Report Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Report Content</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="report-reason-label">Reason for reporting</InputLabel>
            <Select
              labelId="report-reason-label"
              value={reportReason}
              label="Reason for reporting"
              onChange={(e) => setReportReason(e.target.value)}
            >
              {Object.entries(REPORT_REASONS).map(([key, value]) => (
                <MenuItem key={value} value={value}>
                  {REPORT_REASON_LABELS[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReport} 
            variant="contained" 
            color="primary"
            disabled={!reportReason}
          >
            Submit Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Moderation Menu */}
      <Menu
        anchorEl={moderationMenuAnchor}
        open={Boolean(moderationMenuAnchor)}
        onClose={() => {
          setModerationMenuAnchor(null);
          setSelectedContentForModeration(null);
        }}
      >
        <MenuItem onClick={() => handleModerateContent('spam')}>
          Remove as Spam
        </MenuItem>
        <MenuItem onClick={() => handleModerateContent('offensive')}>
          Remove as Offensive
        </MenuItem>
        <MenuItem onClick={() => handleModerateContent('off-topic')}>
          Remove as Off-Topic
        </MenuItem>
      </Menu>

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeletePost}
        item={post?.topic || 'this post'}
      />
    </>
  );
};

export default ThreadView;
